import type { ReactNode } from "react";

type CardProps = { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>;

export function Card({ children, className = "", ...rest }: CardProps) {
  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white shadow-sm dark:border-[#2e2e2e] dark:bg-[#1a1a1a] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

type ButtonProps = {
  children: ReactNode;
  variant?: "primary" | "secondary" | "danger";
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const VARIANTS: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-emerald-600 text-white hover:bg-emerald-700",
  secondary:
    "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-[#2e2e2e] dark:bg-[#1a1a1a] dark:text-gray-300 dark:hover:bg-[#222]",
  // Destructive actions use amber, not red — the design system has no red.
  danger: "bg-amber-600 text-white hover:bg-amber-700",
};

export function Button({ children, variant = "primary", className = "", ...rest }: ButtonProps) {
  return (
    <button
      className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${VARIANTS[variant]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex justify-center py-10" data-testid="spinner">
      <div
        role="status"
        aria-label={label}
        className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600 dark:border-[#2e2e2e] dark:border-t-emerald-400"
      />
    </div>
  );
}

export function ErrorMessage({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
      <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="error-message">
        {error.message}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400"
          data-testid="error-retry"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  testId,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <Card className="p-8 text-center">
      <p className="font-medium text-gray-900 dark:text-gray-100" data-testid={testId}>
        {title}
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-600 dark:text-gray-400">
        {description}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

export function PageHeading({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex items-center justify-between gap-4">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      {action}
    </div>
  );
}
